import React, { useMemo, useState } from 'react'
import { router } from '@inertiajs/react'
import { AlertTriangle, CheckCircle2, ExternalLink, Link2, Plus, RotateCcw, Trash2, Users } from 'lucide-react'
import OperatorLayout from '@/layouts/OperatorLayout'
import MarketIntelligenceNav from '@/components/MarketIntelligenceNav'
import { HelpTooltip } from '@/components/HelpTooltip'
import DashboardCard, { CardSlot } from '@/components/dashboard/DashboardCard'
import CompetitorBadge from '@/components/market/CompetitorBadge'
import { MARKET_GUIDE_PATH } from '@/components/market/MarketHeader'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { parseUrls } from '@/lib/market'
import type { MarketCompsetEntry, MarketCompsetProps } from '@/types/market'

/**
 * MARKET INTELLIGENCE, COMPSET. Setup, not reading: your own Booking.com listing, the room whose
 * price is held against the compset, and the competitors themselves. Every label and sentence is
 * composed server-side (BuildMarketCompsetMutation). Edit controls render only for someone who may
 * change pricing (`canManage`); the server refuses the rest anyway.
 */

function ListingCard({
  listing,
  canManage,
  onLink,
}: {
  listing: MarketCompsetProps['listing']
  canManage: boolean
  onLink: (url: string, done: () => void) => void
}) {
  const [url, setUrl] = useState('')
  const [saving, setSaving] = useState(false)

  return (
    <DashboardCard
      title="Your Booking.com listing"
      description="Your own listing is checked with your competitors and is never counted as one."
      icon={<Link2 className="h-4 w-4 shrink-0 text-muted-foreground" />}
    >
      {listing.linked ? (
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
          <div className="min-w-0 space-y-1">
            <p className="text-sm font-medium text-foreground">{listing.name ?? 'Linked'}</p>
            {(listing.starsLabel || listing.reviewLabel) && (
              <p className="text-xs text-muted-foreground">
                {[listing.starsLabel, listing.reviewLabel].filter(Boolean).join(' · ')}
              </p>
            )}
            <p className="text-xs text-muted-foreground">{listing.sentence}</p>
            {listing.bookingComUrl && (
              <a
                href={listing.bookingComUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex max-w-full cursor-pointer items-center gap-1 rounded text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <span className="truncate">{listing.bookingComUrl}</span>
                <ExternalLink className="h-3 w-3 shrink-0" />
                <span className="sr-only">(opens Booking.com in a new tab)</span>
              </a>
            )}
          </div>
        </div>
      ) : canManage ? (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (!url.trim()) return
            setSaving(true)
            onLink(url.trim(), () => {
              setSaving(false)
              setUrl('')
            })
          }}
          className="space-y-3"
        >
          <p className="text-xs text-muted-foreground">{listing.sentence}</p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-2">
              <Label htmlFor="booking-url">Booking.com URL</Label>
              <Input
                id="booking-url"
                type="url"
                placeholder="https://www.booking.com/hotel/gb/your-hotel.html"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                required
              />
            </div>
            <Button type="submit" disabled={saving || !url.trim()} className="rounded-lg">
              {saving ? 'Linking...' : 'Link your listing'}
            </Button>
          </div>
        </form>
      ) : (
        <p className="text-sm text-muted-foreground">{listing.sentence}</p>
      )}
    </DashboardCard>
  )
}

function ComparisonRoomCard({
  comparison,
  canManage,
}: {
  comparison: MarketCompsetProps['comparison']
  canManage: boolean
}) {
  const effective = comparison.options.find((o) => o.isEffective)
  const [draft, setDraft] = useState<string>(effective ? String(effective.roomTypeId) : '')
  const [saving, setSaving] = useState(false)
  const chosen = comparison.options.find((o) => String(o.roomTypeId) === draft)
  const dirty = effective ? draft !== String(effective.roomTypeId) : draft !== ''

  const save = (roomTypeId: number | '') => {
    setSaving(true)
    router.patch(
      comparison.updateHref,
      { room_type_id: roomTypeId },
      { preserveScroll: true, onFinish: () => setSaving(false) }
    )
  }

  return (
    <DashboardCard
      title="Comparison room"
      description="The room whose price the Overview holds against your competitors' list prices."
    >
      <div className="space-y-3">
        <p className="text-sm font-medium text-foreground">{comparison.sentence}</p>
        {comparison.detail && !comparison.choiceUnavailable && (
          <p className="text-xs text-muted-foreground">{comparison.detail}</p>
        )}
        {comparison.choiceUnavailable && comparison.detail && (
          <p className="flex items-start gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-inset ring-amber-200">
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
            {comparison.detail}
          </p>
        )}

        {canManage && comparison.options.length > 0 && (
          <div className="space-y-2 pt-1">
            <Label htmlFor="comparison-room">Room</Label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Select value={draft} onValueChange={setDraft}>
                <SelectTrigger id="comparison-room" className="sm:max-w-md">
                  <SelectValue placeholder="Choose a room" />
                </SelectTrigger>
                <SelectContent>
                  {comparison.options.map((option) => (
                    <SelectItem key={option.roomTypeId} value={String(option.roomTypeId)} disabled={!option.priceable}>
                      <span className="flex items-center gap-2">
                        {option.label}
                        {option.isDefault && (
                          <span className="rounded-full bg-slate-100 px-1.5 py-px text-[10px] font-semibold text-slate-600">
                            Default
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                onClick={() => chosen && save(chosen.roomTypeId)}
                disabled={!dirty || !chosen || !chosen.priceable || saving}
                className="rounded-lg"
              >
                {saving ? 'Saving...' : 'Compare this room'}
              </Button>
            </div>
            {chosen?.linkedToName && <p className="text-xs text-muted-foreground">Linked to {chosen.linkedToName}.</p>}
            {chosen?.note && (
              <p className="flex items-start gap-1.5 text-xs text-amber-700">
                <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
                {chosen.note}
              </p>
            )}
            {!comparison.isDefault && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => save('')}
                disabled={saving}
                className="-ml-2 h-8 rounded-lg text-xs text-muted-foreground hover:text-foreground"
              >
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                Use the default room
              </Button>
            )}
          </div>
        )}
      </div>
    </DashboardCard>
  )
}

function EntryName({ entry }: { entry: MarketCompsetEntry }) {
  return (
    <a
      href={entry.bookingComUrl}
      target="_blank"
      rel="noopener noreferrer"
      title={`${entry.name} on Booking.com`}
      className="inline-flex min-w-0 cursor-pointer items-center gap-1 rounded text-sm font-medium text-foreground hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
    >
      <span className="truncate">{entry.name}</span>
      <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
      <span className="sr-only">(opens Booking.com in a new tab)</span>
    </a>
  )
}

/** You for your own listing, a dashed ring for another listing of this building, else the competitor's number. */
function EntryBadge({ entry, number }: { entry: MarketCompsetEntry; number: number | undefined }) {
  if (entry.isOwnListing) return <CompetitorBadge isYou />
  if (entry.isBuildingListing) return <CompetitorBadge isBuilding />
  return <CompetitorBadge number={number} />
}

function RemoveButton({
  entry,
  onRemove,
}: {
  entry: MarketCompsetEntry
  onRemove: (entry: MarketCompsetEntry) => void
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      aria-label={`Remove ${entry.name}`}
      title={`Remove ${entry.name}`}
      onClick={() => onRemove(entry)}
      className="h-8 w-8 rounded-lg p-0 text-rose-500 hover:text-rose-600"
    >
      <Trash2 className="h-3.5 w-3.5" />
    </Button>
  )
}

export default function Compset({
  currentUser,
  property,
  properties,
  compsetEntries,
  listing,
  comparison,
  canManage,
}: MarketCompsetProps) {
  const base = property.basePath
  const updatePath = `${base}/market-intelligence/compset`

  const active = compsetEntries.filter((e) => e.active)
  // A listing of this building is never offered back: the server refuses it as a competitor.
  const removed = compsetEntries.filter((e) => !e.active && !e.isBuildingListing)
  // The same numbers the Overview uses: competitors in compset order, this building's listings never counted.
  const numbers = useMemo(() => {
    const map = new Map<number, number>()
    active.filter((e) => !e.isBuildingListing).forEach((e, i) => map.set(e.id, i + 1))
    return map
  }, [active])
  const competitorCount = numbers.size

  const [addOpen, setAddOpen] = useState(false)
  const [addText, setAddText] = useState('')
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<MarketCompsetEntry | null>(null)
  const [removeBusy, setRemoveBusy] = useState(false)
  const urls = parseUrls(addText)

  const link = (url: string, done: () => void) => {
    router.post(
      `${base}/market-intelligence/link-booking-com`,
      { booking_com_url: url },
      { preserveScroll: true, onFinish: done }
    )
  }

  const add = (list: string[], done?: () => void) => {
    router.patch(updatePath, { add_booking_com_urls: list }, { preserveScroll: true, onFinish: done })
  }

  const confirmRemove = () => {
    if (!removing) return
    setRemoveBusy(true)
    router.patch(
      updatePath,
      { remove_market_property_ids: [removing.marketPropertyId] },
      {
        preserveScroll: true,
        onFinish: () => {
          setRemoveBusy(false)
          setRemoving(null)
        },
      }
    )
  }

  const addButton = canManage && (
    <Button onClick={() => setAddOpen(true)} size="sm" className="h-8 rounded-lg">
      <Plus className="mr-1.5 h-3.5 w-3.5" />
      Add competitor
    </Button>
  )

  return (
    <OperatorLayout title="Compset" currentUser={currentUser} property={property} properties={properties}>
      <div className="space-y-6">
        <div className="space-y-4">
          <div className="animate-slide-in" style={{ animationDelay: '0ms' }}>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold tracking-tight text-foreground">Market Intelligence</h1>
              <HelpTooltip
                content="Your compset is the hotels you compare with. Add a competitor by pasting its Booking.com address. Link your own listing first so it is never counted as a competitor."
                learnMore={MARKET_GUIDE_PATH}
              />
            </div>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Who you compare with, and which of your rooms is compared
            </p>
          </div>
          <MarketIntelligenceNav propertyBasePath={base} activeTab="compset" />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <CardSlot delay={100} className="min-w-0">
            <ListingCard listing={listing} canManage={canManage} onLink={link} />
          </CardSlot>
          <CardSlot delay={150} className="min-w-0">
            <ComparisonRoomCard comparison={comparison} canManage={canManage} />
          </CardSlot>
        </div>

        <CardSlot delay={200}>
          {active.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm">
              <Users className="mx-auto h-8 w-8 text-muted-foreground/40" />
              <p className="mt-3 text-sm font-medium text-foreground">No competitors yet</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {canManage
                  ? 'Paste the Booking.com address of a hotel you compete with. Its list prices are checked twice a day from then on.'
                  : 'Someone who manages pricing for this hotel can add competitors here.'}
              </p>
              {canManage && (
                <Button onClick={() => setAddOpen(true)} variant="outline" size="sm" className="mt-4 rounded-lg">
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  Add competitor
                </Button>
              )}
            </div>
          ) : (
            <DashboardCard
              title="Competitors"
              description={`${competitorCount} ${competitorCount === 1 ? 'competitor' : 'competitors'} in your compset`}
              action={addButton || undefined}
              padded={false}
            >
              <div className="hidden overflow-x-auto md:block">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="pl-5">Hotel</TableHead>
                      <TableHead>Stars</TableHead>
                      <TableHead>Reviews</TableHead>
                      <TableHead>Distance</TableHead>
                      <TableHead>Tonight</TableHead>
                      <TableHead>Last checked</TableHead>
                      {canManage && (
                        <TableHead className="w-12 pr-5">
                          <span className="sr-only">Remove</span>
                        </TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {active.map((entry) => (
                      <TableRow key={entry.id} data-testid={`compset-row-${entry.id}`}>
                        <TableCell className="max-w-[300px] pl-5">
                          <div className="flex min-w-0 items-center gap-2">
                            <EntryBadge entry={entry} number={numbers.get(entry.id)} />
                            <EntryName entry={entry} />
                          </div>
                          {entry.notCountedLabel && (
                            <p
                              className={cn(
                                'mt-0.5 text-[11px] text-muted-foreground',
                                entry.isOwnListing ? 'pl-9' : 'pl-6'
                              )}
                            >
                              {entry.notCountedLabel}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm text-foreground">{entry.starsLabel}</TableCell>
                        <TableCell className="whitespace-nowrap text-sm text-foreground">
                          {entry.reviewLabel ?? <span className="text-muted-foreground">--</span>}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                          {entry.distanceLabel ?? '--'}
                        </TableCell>
                        <TableCell
                          className={cn(
                            'whitespace-nowrap text-sm',
                            entry.tonightPrice != null
                              ? 'font-medium tabular-nums text-foreground'
                              : 'text-muted-foreground'
                          )}
                        >
                          {entry.tonightLabel ?? '--'}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                          {entry.lastCheckedLabel}
                        </TableCell>
                        {canManage && (
                          <TableCell className="pr-5 text-right">
                            {!entry.isOwnListing && <RemoveButton entry={entry} onRemove={setRemoving} />}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <ul className="divide-y divide-border/40 md:hidden">
                {active.map((entry) => (
                  <li key={entry.id} className="flex items-start gap-3 px-4 py-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-2">
                        <EntryBadge entry={entry} number={numbers.get(entry.id)} />
                        <EntryName entry={entry} />
                      </div>
                      {entry.notCountedLabel && (
                        <p className="mt-1 text-[11px] text-muted-foreground">{entry.notCountedLabel}</p>
                      )}
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        {[entry.distanceLabel, entry.starsLabel, entry.reviewLabel].filter(Boolean).join(' · ')}
                      </p>
                      <p className="mt-1.5 text-xs font-medium text-foreground">{entry.tonightLabel ?? '--'}</p>
                      <p className="text-[11px] text-muted-foreground">{entry.lastCheckedLabel}</p>
                    </div>
                    {canManage && !entry.isOwnListing && <RemoveButton entry={entry} onRemove={setRemoving} />}
                  </li>
                ))}
              </ul>

              {removed.length > 0 && (
                <div className="border-t border-border/40 px-5 py-4">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Removed</p>
                  <ul className="mt-2 flex flex-wrap gap-2">
                    {removed.map((entry) => (
                      <li
                        key={entry.id}
                        className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-slate-50 py-1 pl-3 pr-1 text-xs text-muted-foreground"
                      >
                        {entry.name}
                        {canManage && (
                          <button
                            type="button"
                            onClick={() => add([entry.bookingComUrl])}
                            className="inline-flex h-6 cursor-pointer items-center gap-1 rounded-full px-2 text-[11px] font-medium text-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                          >
                            <RotateCcw className="h-3 w-3" />
                            Add back
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </DashboardCard>
          )}
        </CardSlot>
      </div>

      {/* Add competitors */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Add competitor</DialogTitle>
            <DialogDescription>Paste one or more Booking.com addresses, one per line.</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (urls.length === 0) return
              setAdding(true)
              add(urls, () => {
                setAdding(false)
                setAddOpen(false)
                setAddText('')
              })
            }}
            className="flex min-h-0 flex-1 flex-col"
          >
            <DialogBody className="space-y-2">
              <Label htmlFor="competitor-urls">Booking.com addresses</Label>
              <textarea
                id="competitor-urls"
                rows={4}
                value={addText}
                onChange={(e) => setAddText(e.target.value)}
                placeholder={'https://www.booking.com/hotel/gb/a-hotel-nearby.html'}
                className="flex w-full resize-y rounded-lg border border-border/60 bg-white px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:border-primary/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
              />
              <p className="text-xs text-muted-foreground">
                Open the hotel on Booking.com and copy the address from your browser. Their list prices are checked from
                the next check.
              </p>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)} className="rounded-lg">
                Cancel
              </Button>
              <Button type="submit" disabled={adding || urls.length === 0} className="rounded-lg">
                {adding ? 'Adding...' : urls.length > 1 ? `Add ${urls.length} competitors` : 'Add competitor'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Remove a competitor */}
      <AlertDialog open={removing != null} onOpenChange={(open) => !open && !removeBusy && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removing?.name} from your compset?</AlertDialogTitle>
            <AlertDialogDescription>
              It leaves every comparison on the Overview straight away. You can add it back at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel type="button" onClick={() => setRemoving(null)} disabled={removeBusy}>
              Keep it
            </AlertDialogCancel>
            <AlertDialogAction
              type="button"
              onClick={confirmRemove}
              disabled={removeBusy}
              className="bg-rose-600 text-white hover:bg-rose-700"
            >
              {removeBusy ? 'Removing...' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </OperatorLayout>
  )
}
